import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import Controls from "../../../components/controls/Controls";
import { getAllowedChildTypeOptions, getRootTypeOptions, parseModelYear } from "../assetHierarchyUtils";
import { getAssetHierarchyPalette, getContainedButtonSx, getDialogSectionBoxSx, getOutlinedButtonSx } from "../assetHierarchyStyles";
import cmmsMessages, { getAssetNodeTypeLabel } from "../cmmsMessages";
import messages from "../messages";

// A root has no parent, so the type choice cannot come from one. The mode carries that
// instead of being inferred from a missing parentNode, which also means "nothing selected".
const getTypeOptionsForMode = (mode, parentNode) =>
  mode === "createRoot" ? getRootTypeOptions() : getAllowedChildTypeOptions(parentNode);

const resolveTitle = (isEdit, isRoot) => {
  if (isEdit) return messages.editNodeDialogTitle;
  return isRoot ? messages.addRootDialogTitle : messages.addNodeDialogTitle;
};

const resolveIntro = (isEdit, isRoot) => {
  if (isEdit) return messages.editNodeIntro;
  return isRoot ? messages.addRootIntro : messages.addNodeIntro;
};

// The nameplate ("maskinkort") the API accepts on a maintenance object only. Blank is "" while
// the dialog is open; handleSave turns the year into a number or null.
const emptyNameplate = { manufacturer: "", model: "", modelYear: "", serialNumber: "" };

// AxionTextBox hands its inputProps to MUI's InputProps, so the native attributes sit one level
// down. 100 is the column width in the database.
const nameplateTextInputProps = { inputProps: { maxLength: 100 } };
const modelYearInputProps = { inputProps: { inputMode: "numeric", maxLength: 4 } };

const createInitialFormState = (mode, selectedNode, parentNode) => {
  if (mode === "edit" && selectedNode) {
    return {
      id: selectedNode.id,
      parentAssetNodeId: selectedNode.parentAssetNodeId,
      nodeType: selectedNode.nodeType,
      name: selectedNode.name ?? "",
      description: selectedNode.description ?? "",
      sortOrder: selectedNode.sortOrder ?? 0,
      isActive: selectedNode.isActive,
      machineId: "",
      manufacturer: selectedNode.manufacturer ?? "",
      model: selectedNode.model ?? "",
      modelYear: selectedNode.modelYear ?? "",
      serialNumber: selectedNode.serialNumber ?? "",
    };
  }

  const allowedTypes = getTypeOptionsForMode(mode, parentNode);
  return {
    // Null, not "", for a root: it is sent to the API as the absence of a parent.
    parentAssetNodeId: mode === "createRoot" ? null : parentNode?.id ?? "",
    nodeType: allowedTypes[0]?.id ?? "",
    name: "",
    description: "",
    sortOrder: 0,
    isActive: true,
    machineId: "",
    ...emptyNameplate,
  };
};

export default function AssetNodeFormDialog({ machineOptions, mode, onClose, onSave, open, parentNode, selectedNode }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [formState, setFormState] = useState(createInitialFormState(mode, selectedNode, parentNode));
  const [isNameEdited, setIsNameEdited] = useState(false);
  const isEdit = mode === "edit";
  const isRoot = mode === "createRoot";
  const nodeTypeOptions = useMemo(
    // Titles come from assetHierarchyUtils in English; relabel via the shared enum helper.
    () => getTypeOptionsForMode(mode, parentNode).map((option) => ({
      ...option,
      title: getAssetNodeTypeLabel(option.id, intl),
    })),
    [mode, parentNode, intl],
  );
  const isMaintenanceObject = Number(formState.nodeType) === 3;
  const availableMachineOptions = useMemo(() => {
    return (machineOptions ?? [])
      .filter((machine) => !machine.assetNodeId)
      .map((machine) => ({ id: machine.id, title: machine.machineName }));
  }, [machineOptions]);

  useEffect(() => {
    if (open) {
      setFormState(createInitialFormState(mode, selectedNode, parentNode));
      setIsNameEdited(false);
    }
  }, [mode, open, parentNode, selectedNode]);

  const handleChange = (event) => {
    const { name, id, value, type, checked } = event.target;
    const fieldName = name || id;

    if (fieldName === "name") {
      setIsNameEdited(true);
    }

    setFormState((current) => ({
      ...current,
      [fieldName]: type === "checkbox" ? checked : value,
      // Leaving maintenance object drops what only a maintenance object may carry, or the API
      // would refuse the save over fields the user can no longer see.
      ...(fieldName === "nodeType" && Number(value) !== 3 ? { machineId: "", ...emptyNameplate } : {}),
    }));
  };

  const handleMachineChange = (event) => {
    const { value } = event.target;
    const selectedMachine = (machineOptions ?? []).find((machine) => machine.id === Number(value));

    setFormState((current) => ({
      ...current,
      machineId: value,
      name: selectedMachine && !isNameEdited ? selectedMachine.machineName : current.name,
    }));
  };

  const handleSave = () => {
    onSave({
      ...formState,
      nodeType: Number(formState.nodeType),
      sortOrder: Number(formState.sortOrder || 0),
      machineId: formState.machineId ? Number(formState.machineId) : null,
      modelYear: parseModelYear(formState.modelYear),
    });
  };

  return (
    <Popup
      openPopup={open}
      setOpenPopup={onClose}
      maxWidth="sm"
      title={intl.formatMessage(resolveTitle(isEdit, isRoot))}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box sx={getDialogSectionBoxSx(cmmsColors)}>
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
            <FormattedMessage {...resolveIntro(isEdit, isRoot)} />
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gap: 2 }}>
        {!isEdit &&
          <AxionControls.AxionSelect
            name="nodeType"
            label={intl.formatMessage(messages.fieldType)}
            value={formState.nodeType}
            onChange={handleChange}
            options={nodeTypeOptions}
            showNoneOption={false}
          />
        }
        {!isEdit && isMaintenanceObject &&
          <AxionControls.AxionSelect
            name="machineId"
            label={intl.formatMessage(messages.machineLinkField)}
            value={formState.machineId}
            onChange={handleMachineChange}
            options={availableMachineOptions}
            noneText={intl.formatMessage(messages.customObject)}
            searchField
          />
        }
        <AxionControls.AxionTextBox
          key={`asset-node-name-${formState.id ?? "new"}-${formState.machineId || "custom"}`}
          name="name"
          label={intl.formatMessage(messages.fieldName)}
          value={formState.name}
          onChange={handleChange}
        />
        <AxionControls.AxionMultiLineText
          key={`asset-node-description-${formState.id ?? "new"}`}
          name="description"
          label={intl.formatMessage(messages.fieldDescription)}
          value={formState.description}
          rows={3}
          onChange={handleChange}
        />
        {isMaintenanceObject &&
          <Box>
            <Typography variant="subtitle2" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
              <FormattedMessage {...messages.machineCardTitle} />
            </Typography>
            <Box sx={{ display: "grid", columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
              <AxionControls.AxionTextBox
                key={`asset-node-manufacturer-${formState.id ?? "new"}`}
                name="manufacturer"
                label={intl.formatMessage(messages.fieldManufacturer)}
                value={formState.manufacturer}
                inputProps={nameplateTextInputProps}
                onChange={handleChange}
              />
              <AxionControls.AxionTextBox
                key={`asset-node-model-${formState.id ?? "new"}`}
                name="model"
                label={intl.formatMessage(messages.fieldModel)}
                value={formState.model}
                inputProps={nameplateTextInputProps}
                onChange={handleChange}
              />
              <AxionControls.AxionTextBox
                key={`asset-node-model-year-${formState.id ?? "new"}`}
                name="modelYear"
                label={intl.formatMessage(messages.fieldModelYear)}
                value={formState.modelYear}
                inputProps={modelYearInputProps}
                onChange={handleChange}
              />
              <AxionControls.AxionTextBox
                key={`asset-node-serial-${formState.id ?? "new"}`}
                name="serialNumber"
                label={intl.formatMessage(messages.fieldSerialNumber)}
                value={formState.serialNumber}
                inputProps={nameplateTextInputProps}
                onChange={handleChange}
              />
            </Box>
          </Box>
        }
        <AxionControls.AxionTextBox
          key={`asset-node-sort-${formState.id ?? "new"}`}
          name="sortOrder"
          label={intl.formatMessage(messages.fieldSortOrder)}
          value={formState.sortOrder}
          onChange={handleChange}
        />
        {isEdit && selectedNode?.nodeType !== 1 &&
          <Controls.Checkbox
            name="isActive"
            label={intl.formatMessage(messages.fieldActive)}
            value={formState.isActive}
            onChange={handleChange}
            disabled={selectedNode?.isActive}
          />
        }
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={onClose} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSave} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
    </Popup>
  );
}
